"use client"

import { useEffect, useMemo, useState } from "react"

import {
  cn,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogTitle,
  Divider,
} from "@coval/ui"
import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
} from "cmdk"
import { CornerDownLeft, MessageSquare, Pin, Search, X } from "lucide-react"

import { formatRelativeTimestamp } from "@/utils/formatting/format-timestamp"

import type { CoviThreadMeta } from "../model/types"

/** Every row's leading glyph and the meta column's own icon. */
const ROW_ICON = "size-4 shrink-0 text-coval-text-muted"

/**
 * Chat search, as a palette over the page rather than a field inside the nav.
 *
 * The sidebar's chat section is 240px wide and five rows tall — enough to get
 * back to a conversation the user was just in, and nowhere near enough to read
 * a search of the whole history in. So searching leaves the nav entirely: the
 * icon on the "Chats" row opens this, the nav stays exactly as it was behind
 * it, and the matches get a full-width surface where a chat's title has room to
 * be read and its date sits beside it.
 *
 * Keyboard-first, because a palette that has to be clicked is just a narrower
 * list: the field takes focus on open, the arrows walk the matches, Enter opens
 * the highlighted one (its row swaps its date for the ⏎ that says so), and
 * Escape closes without leaving the chat the user was already in.
 */
export function CoviThreadSearchDialog({
  open,
  onOpenChange,
  threads,
  onSelect,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  threads: CoviThreadMeta[]
  onSelect: (threadId: string) => void
}) {
  const [query, setQuery] = useState("")

  // Every open is a fresh search. A palette that reopened holding the last
  // query would answer a question the user asked minutes ago.
  useEffect(() => {
    if (open) {
      setQuery("")
    }
  }, [open])

  // Pinned first, then newest — the order the nav lists them in, so an empty
  // field shows the history the user already knows rather than a reshuffle.
  const ordered = useMemo(
    () =>
      [...threads].sort(
        (a, b) =>
          Number(b.pinned) - Number(a.pinned) || b.updatedAt - a.updatedAt,
      ),
    [threads],
  )

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        size="lg"
        className="max-h-[min(70vh,560px)] gap-0 overflow-hidden p-0"
      >
        {/* The field itself is the heading here — a title row above it would
            spend the top of the palette saying what the placeholder says. */}
        <DialogTitle className="sr-only">Search chats</DialogTitle>
        <DialogDescription className="sr-only">
          Find one of your Sofia chats by name and open it.
        </DialogDescription>
        <Command
          loop
          // cmdk names the field from its own hidden label, so the label has to
          // be given here or the input ends up with no accessible name at all.
          label="Search chats"
          // The nav list's own matching rule rather than cmdk's fuzzy scorer: a
          // chat matches when its title contains what was typed. Rows carry
          // their id as the cmdk value (titles repeat, ids do not) and their
          // title as the only keyword, so the id is never itself searchable.
          filter={(_value, search, keywords) => {
            const title = keywords?.[0] ?? ""
            const needle = search.trim().toLowerCase()
            return title.toLowerCase().includes(needle) ? 1 : 0
          }}
          className="flex min-h-0 flex-col"
        >
          <div className="flex shrink-0 items-center gap-3 px-4 py-3">
            <Search className="size-5 shrink-0 text-coval-text-muted" />
            <CommandInput
              value={query}
              onValueChange={setQuery}
              placeholder="Search chats"
              aria-label="Search chats"
              className="min-w-0 flex-1 bg-transparent text-base text-coval-text outline-none placeholder:text-coval-text-muted"
            />
            <DialogClose
              aria-label="Close search"
              className="flex size-6 shrink-0 items-center justify-center rounded text-coval-text-muted transition-colors hover:bg-surface-1 hover:text-coval-text"
            >
              <X className="size-5" />
            </DialogClose>
          </div>
          <Divider />
          <CommandList className="min-h-0 flex-1 overflow-y-auto p-2">
            <CommandEmpty className="px-3 py-10 text-center text-sm text-coval-text-muted">
              No chats match that search
            </CommandEmpty>
            {ordered.map((thread) => (
              <CommandItem
                key={thread.id}
                value={thread.id}
                keywords={[thread.title]}
                onSelect={() => {
                  onOpenChange(false)
                  onSelect(thread.id)
                }}
                className={cn(
                  "group flex cursor-pointer items-center gap-3 rounded-md px-3 py-2",
                  "transition-colors aria-selected:bg-surface-1",
                )}
              >
                {/* A pinned chat says so where every other chat carries the
                    chat glyph, matching how the nav list marks them. */}
                {thread.pinned ? (
                  <Pin aria-label="Pinned" className={ROW_ICON} />
                ) : (
                  <MessageSquare aria-hidden className={ROW_ICON} />
                )}
                <span className="min-w-0 flex-1 truncate text-sm text-coval-text">
                  {thread.title}
                </span>
                {/* One meta column, holding the chat's date until the row is
                    the highlighted one — where what matters is that Enter
                    opens it. */}
                <span className="shrink-0 text-xs text-coval-text-muted group-aria-selected:hidden">
                  {formatRelativeTimestamp(
                    new Date(thread.updatedAt).toISOString(),
                  )}
                </span>
                <CornerDownLeft
                  aria-hidden
                  className={cn(ROW_ICON, "hidden group-aria-selected:block")}
                />
              </CommandItem>
            ))}
          </CommandList>
        </Command>
      </DialogContent>
    </Dialog>
  )
}
